import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { patientsApi } from '@/api/patients';
import PatientDialog from '@/components/patients/PatientDialog';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { Patient } from '@/types/patient';
import { calculateAgeAtExamDate } from '@/utils/getAgeAtExamDate';
import { resolvePatientAge } from '@/utils/resolvePatientAge';
import { ChoosePatientPanel } from './patient-info/ChoosePatientPanel';
import { EvaluationPeriodPanel } from './patient-info/EvaluationPeriodPanel';
import type { ExamDateField } from './patient-info/EvaluationPeriodPanel';
import type { WizardStepProps } from './types';
import type { ReportData } from '../preview/types';

type ReportPatient = NonNullable<ReportData['patient']>;

const buildAgeFields = (
    patientDateOfBirth?: string,
    examDate?: string,
    fallbackAge?: number | string,
): Pick<ReportPatient, 'age' | 'age_months' | 'age_days'> => {
    const resolvedAge = resolvePatientAge({
        dateOfBirth: patientDateOfBirth,
        examDate,
        fallbackAge,
    });

    if (!patientDateOfBirth || !examDate) {
        return {
            age: resolvedAge,
            age_months: undefined,
            age_days: undefined,
        };
    }

    const ageData = calculateAgeAtExamDate(patientDateOfBirth, examDate);

    if (
        !ageData ||
        !Number.isFinite(ageData.years) ||
        !Number.isFinite(ageData.months) ||
        !Number.isFinite(ageData.days) ||
        ageData.years < 0 ||
        ageData.months < 0 ||
        ageData.days < 0
    ) {
        return {
            age: resolvedAge,
            age_months: undefined,
            age_days: undefined,
        };
    }

    return {
        age: ageData.years,
        age_months: ageData.months,
        age_days: ageData.days,
    };
};

/**
 * The patient step, which is really two steps wearing one name.
 *
 * Before the report exists the editor is waiting on a single decision — which
 * patient — and every other section defers to this one until it is made. After
 * the report exists that decision is locked, and what is left is the evaluation
 * period. Neither state carries a save button: choosing the patient creates the
 * report, and from then on the header, Ctrl+S and the autosave keep it.
 */
export default function PatientInfoStep(_props: WizardStepProps) {
    // Use the context which provides the functional handleUpdate
    const { formData: data, handleUpdate, reportId } = useReportWizard();
    const [showPatientDialog, setShowPatientDialog] = useState(false);
    const activePatientAccess = useFeatureEntitlement('patients.active');
    const createReportAccess = useFeatureEntitlement('reports.create');

    // `reportId` comes from the URL and is set before the fetch resolves, so
    // opening an existing report never flashes the pre-creation panel — the same
    // signal `StepContentSidebar` blocks the other steps on. Note this is not the
    // same question as "is the patient locked": while the creation prompt is on
    // screen the patient is already chosen but the report is not created yet, and
    // that moment still belongs to the pre-creation panel.
    const isReportCreated = Boolean(reportId) || Boolean(data.id);

    // `isFetched`, not `isLoading`: `initialData` makes React Query report the
    // query as settled from the first render, so `isLoading` is never true here
    // and the panel would take the empty `initialData` for a real empty list.
    const { data: patients, isFetched } = useQuery<Patient[]>({
        queryKey: ['patients'],
        queryFn: () => patientsApi.list_available(),
        initialData: [],
        enabled: !isReportCreated,
    });

    const buildReportPatient = useCallback(
        (patient: Patient): ReportPatient => {
            const ageFields = buildAgeFields(
                patient.date_of_birth,
                data.tests_performed_at_start,
                patient.age,
            );

            return {
                id: patient.id,
                full_name: patient.full_name,
                date_of_birth: patient.date_of_birth || '',
                gender: patient.gender || '',
                city: patient.city || '',
                education_level: patient.education_level as ReportPatient['education_level'],
                referring_professional: patient.referring_professional || '',
                email: patient.email || '',
                phone: patient.phone || patient.contact_number || '',
                emergency_contact_name: patient.emergency_contact_name || '',
                emergency_contact_phone: patient.emergency_contact_phone || '',
                address: patient.address || '',
                avatar_url: patient.avatar_url,
                ...ageFields,
            };
        },
        [data.tests_performed_at_start],
    );

    const handlePatientCreated = (newPatient: Patient) => {
        handleUpdate({
            patient: buildReportPatient(newPatient),
        });
        setShowPatientDialog(false);
    };

    const handleAddNewPatient = () => {
        if (activePatientAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(activePatientAccess.decision));
            return;
        }

        setShowPatientDialog(true);
    };

    const handlePatientSelect = (value: string) => {
        if (value === 'new_patient') {
            handleAddNewPatient();
            return;
        }

        const selectedPatient = patients.find((patient: Patient) => patient.id === value);

        if (selectedPatient) {
            handleUpdate({
                patient: buildReportPatient(selectedPatient),
            });
        }
    };

    const handleExamDateChange = (field: ExamDateField, value: string) => {
        if (field === 'tests_performed_at_start' && data.tests_performed_at_end) {
            const currentEndDate = data.tests_performed_at_end.split('T')[0];

            if (value > currentEndDate) {
                handleUpdate({
                    tests_performed_at_start: value,
                    tests_performed_at_end: '',
                });
                return;
            }
        }
        handleUpdate({ [field]: value });
    };

    const maxDate = format(new Date(), 'yyyy-MM-dd');

    const recalculatePatient = useCallback(() => {
        if (!data.patient) return;

        const nextAgeFields = buildAgeFields(
            data.patient.date_of_birth,
            data.tests_performed_at_start,
            data.patient.age,
        );

        if (
            nextAgeFields.age !== data.patient.age ||
            nextAgeFields.age_months !== data.patient.age_months ||
            nextAgeFields.age_days !== data.patient.age_days
        ) {
            handleUpdate((prev) => {
                if (!prev.patient) return {};

                return {
                    patient: {
                        ...prev.patient,
                        ...nextAgeFields,
                    },
                };
            });
        }
    }, [data.patient, data.tests_performed_at_start, handleUpdate]);

    // Stays above the branch on purpose: this is the only place
    // `age`/`age_months`/`age_days` are ever computed, so it has to keep running
    // whichever panel is on screen.
    useEffect(() => {
        recalculatePatient();
    }, [recalculatePatient]);

    if (isReportCreated) {
        return (
            <EvaluationPeriodPanel
                patient={data.patient}
                startDate={data.tests_performed_at_start}
                endDate={data.tests_performed_at_end}
                maxDate={maxDate}
                onExamDateChange={handleExamDateChange}
            />
        );
    }

    return (
        <>
            <ChoosePatientPanel
                patients={patients}
                hasLoadedPatients={isFetched}
                selectedPatientId={data.patient?.id || ''}
                onSelectPatient={handlePatientSelect}
                onAddNewPatient={handleAddNewPatient}
                addPatientBlockedPlan={
                    activePatientAccess.isUnavailableInPlan
                        ? activePatientAccess.availableFromPlan
                        : null
                }
                createReportBlockedPlan={
                    createReportAccess.shouldBlock ? createReportAccess.availableFromPlan : null
                }
                createReportBlockedMessage={getEntitlementBlockedMessage(
                    createReportAccess.decision,
                )}
            />
            <PatientDialog
                open={showPatientDialog}
                onOpenChange={setShowPatientDialog}
                patient={null}
                onPatientCreated={handlePatientCreated}
            />
        </>
    );
}
